'use client';

import { useState, useEffect } from 'react';
import PropertyCard from './PropertyCard';
import PropertyModal from './PropertyModal';
import FilterBar from './FilterBar';

interface Property {
  id: number;
  name: string;
  category: string;
  images: Array<{ url: string; alt: string }>;
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  size: string;
  features: string[];
  description: string;
  pricePerNight: number;
  availability: string;
  amenities: string[];
  virtualTourUrl: string;
}

interface PropertyGalleryInteractiveProps {
  properties: Property[];
  categories: string[];
}

export default function PropertyGalleryInteractive({ properties, categories }: PropertyGalleryInteractiveProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All Properties');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleFilterChange = (filter: string) => {
    if (!isHydrated) return;
    setActiveFilter(filter);
  };

  const handleViewDetails = (id: number) => {
    if (!isHydrated) return;
    const property = properties.find(p => p.id === id);
    if (property) {
      setSelectedProperty(property);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    if (!isHydrated) return;
    setIsModalOpen(false);
    setTimeout(() => setSelectedProperty(null), 300);
  };

  const filteredProperties = activeFilter === 'All Properties'
    ? properties
    : properties.filter(p => p.category === activeFilter);

  return (
    <>
      <FilterBar data-component-id="src/app/property-gallery/components/PropertyGalleryInteractive.tsx:66:6" data-component-path="src/app/property-gallery/components/PropertyGalleryInteractive.tsx" data-component-line="66" data-component-end-line="70" data-component-file="PropertyGalleryInteractive.tsx" data-component-name="FilterBar" data-component-content="%7B%22elementName%22%3A%22FilterBar%22%7D"
        categories={categories}
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
      />

      <section data-component-id="src/app/property-gallery/components/PropertyGalleryInteractive.tsx:72:6" data-component-path="src/app/property-gallery/components/PropertyGalleryInteractive.tsx" data-component-line="72" data-component-end-line="92" data-component-file="PropertyGalleryInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20bg-background%22%7D" className="py-16 bg-background">
        <div data-component-id="src/app/property-gallery/components/PropertyGalleryInteractive.tsx:73:8" data-component-path="src/app/property-gallery/components/PropertyGalleryInteractive.tsx" data-component-line="73" data-component-end-line="91" data-component-file="PropertyGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%22%7D" className="container mx-auto px-4">
          <div data-component-id="src/app/property-gallery/components/PropertyGalleryInteractive.tsx:74:10" data-component-path="src/app/property-gallery/components/PropertyGalleryInteractive.tsx" data-component-line="74" data-component-end-line="82" data-component-file="PropertyGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((property) => (
              <PropertyCard data-component-id="src/app/property-gallery/components/PropertyGalleryInteractive.tsx:76:14" data-component-path="src/app/property-gallery/components/PropertyGalleryInteractive.tsx" data-component-line="76" data-component-end-line="80" data-component-file="PropertyGalleryInteractive.tsx" data-component-name="PropertyCard" data-component-content="%7B%22elementName%22%3A%22PropertyCard%22%7D"
                key={property.id}
                property={property}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {filteredProperties.length === 0 && (
            <div data-component-id="src/app/property-gallery/components/PropertyGalleryInteractive.tsx:85:12" data-component-path="src/app/property-gallery/components/PropertyGalleryInteractive.tsx" data-component-line="85" data-component-end-line="89" data-component-file="PropertyGalleryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%22%7D" className="text-center py-16">
              <p data-component-id="src/app/property-gallery/components/PropertyGalleryInteractive.tsx:86:14" data-component-path="src/app/property-gallery/components/PropertyGalleryInteractive.tsx" data-component-line="86" data-component-end-line="88" data-component-file="PropertyGalleryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xl%20text-muted-foreground%20font-body%22%2C%22textContent%22%3A%22No%20properties%20found%20in%20this%20category.%20Try%20selecting%20a%20different%20filter.%22%7D" className="text-xl text-muted-foreground font-body">
                No properties found in this category. Try selecting a different filter.
              </p>
            </div>
          )}
        </div>
      </section>

      <PropertyModal data-component-id="src/app/property-gallery/components/PropertyGalleryInteractive.tsx:94:6" data-component-path="src/app/property-gallery/components/PropertyGalleryInteractive.tsx" data-component-line="94" data-component-end-line="98" data-component-file="PropertyGalleryInteractive.tsx" data-component-name="PropertyModal" data-component-content="%7B%22elementName%22%3A%22PropertyModal%22%7D"
        property={selectedProperty}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
}